import { useEffect, useMemo, useState, type RefObject } from 'react'
import {
  getDefaultMonths,
  getMonthColumnHighlightBounds,
  getMonthDayFractions,
  isCurrentYear,
  MONTH_COLUMNS
} from '../Gantt.utils'
import { MIN_MONTH_WIDTH, MONTH_COLUMN_GAP } from '../GanttChart.styles'

type UseGanttTimelineMetricsArgs = {
  bodyScrollRef: RefObject<HTMLDivElement | null>
  year: number
  highlightCurrentMonth: boolean
  locale?: string
  months?: string[]
}

const useGanttTimelineMetrics = ({
  bodyScrollRef,
  year,
  highlightCurrentMonth,
  locale,
  months: monthsOverride
}: UseGanttTimelineMetricsArgs) => {
  const [timelineWidth, setTimelineWidth] = useState(
    MIN_MONTH_WIDTH * MONTH_COLUMNS
  )

  const months = useMemo(
    () =>
      monthsOverride?.length === MONTH_COLUMNS
        ? monthsOverride
        : getDefaultMonths(locale),
    [locale, monthsOverride]
  )
  const monthDayFractions = useMemo(() => getMonthDayFractions(year), [year])
  const totalMonthGapWidth = Math.max(0, MONTH_COLUMNS - 1) * MONTH_COLUMN_GAP
  const timelineTrackWidth = Math.max(0, timelineWidth - totalMonthGapWidth)
  const normalizedMonthColumns = useMemo(
    () =>
      monthDayFractions
        .map((fraction) => `${timelineTrackWidth * fraction}px`)
        .join(' '),
    [monthDayFractions, timelineTrackWidth]
  )

  useEffect(() => {
    const bodyNode = bodyScrollRef.current
    if (!bodyNode) return

    const minTimelineWidth = MIN_MONTH_WIDTH * MONTH_COLUMNS
    const syncTimelineWidth = (measuredWidth: number) => {
      const nextWidth = Math.max(minTimelineWidth, measuredWidth)
      setTimelineWidth((currentWidth) =>
        Math.abs(currentWidth - nextWidth) > 0.5 ? nextWidth : currentWidth
      )
    }

    const observer = new ResizeObserver(() => {
      syncTimelineWidth(bodyNode.clientWidth)
    })
    observer.observe(bodyNode)

    syncTimelineWidth(bodyNode.clientWidth)

    return () => observer.disconnect()
  }, [bodyScrollRef])

  const currentMonthIndex =
    highlightCurrentMonth && isCurrentYear(year) ? new Date().getMonth() : -1

  const currentMonthHighlight = useMemo(() => {
    if (currentMonthIndex < 0) return null
    return getMonthColumnHighlightBounds({
      monthIndex: currentMonthIndex,
      timelineWidth,
      monthColumnGap: MONTH_COLUMN_GAP,
      year
    })
  }, [currentMonthIndex, timelineWidth, year])

  return {
    timelineWidth,
    normalizedMonthColumns,
    months,
    currentMonthIndex,
    currentMonthHighlight
  }
}

export default useGanttTimelineMetrics
